import { useId } from "react";

import { shortDate } from "@/shared/format";

/**
 * Hand-rolled SVG line chart for time-series trajectories (no charting dep).
 * Used for BOTH the skill-level trajectory (Skills dashboard + SkillDetail)
 * and the bankroll curve (Tracker) — one component, one design language.
 *
 * Generic over a `{ date, value }` series. The y-axis auto-fits the data range
 * (with padding + a minimum span) so small real movements stay visible. By
 * default it clamps to [0, 100] (skill levels); pass `clamp={null}` for an
 * unbounded value like money. Time is the x-axis (real spacing, not index).
 * Fewer than 2 points → an empty state.
 *
 * The plot stretches to its container with `preserveAspectRatio="none"`, which
 * distorts shapes horizontally — so the line/area/gridlines live in the SVG,
 * but the endpoint dot is an HTML overlay positioned by fraction, keeping it
 * perfectly round at any width.
 */

export interface TrendPoint {
  /** ISO timestamp for the x-axis. */
  date: string;
  /** The plotted value (skill level 0-100, or cumulative money, etc.). */
  value: number;
}

type Clamp = { lo: number; hi: number } | null;

interface TrendChartProps {
  points: TrendPoint[];
  /** Pixel height of the plot area. */
  height?: number;
  /** Show range labels (visible high/low) + a date-range caption. */
  showAxis?: boolean;
  /** Accessible label for the chart. */
  ariaLabel?: string;
  className?: string;
  /** Copy shown when there isn't enough history to draw a line. */
  emptyLabel?: string;
  /** Visible-domain clamp. Defaults to skill levels [0,100]; pass `null` for
   *  an unbounded value (money). */
  clamp?: Clamp;
  /** Smallest visible value span (keeps a near-flat series from filling the
   *  whole height). Defaults to 14 (skill points). */
  minSpan?: number;
  /** Format the endpoint value labels (defaults to a rounded integer). */
  formatValue?: (v: number) => string;
  /** Stroke/area/dot color (CSS color). Defaults to the brand accent. */
  color?: string;
  /** Draw a dashed baseline at value=0 when it falls inside the domain
   *  (useful for a bankroll curve that crosses break-even). */
  showZeroLine?: boolean;
}

// viewBox units — the SVG scales responsively to its container width.
const VW = 300;
const PAD_X = 6;
const PAD_Y = 6;
const DEFAULT_CLAMP: Clamp = { lo: 0, hi: 100 };
const DEFAULT_MIN_SPAN = 14;

interface TrendGeometry {
  line: string;
  area: string;
  /** Visible y-axis domain. */
  domainLo: number;
  domainHi: number;
  /** Start + end positions as fractions of the plot box (for HTML labels). */
  startXFrac: number;
  startYFrac: number;
  endXFrac: number;
  endYFrac: number;
  /** y-fraction of value=0, or null when 0 is outside the visible domain. */
  zeroYFrac: number | null;
}

/** Auto-fit y-domain for a set of values: pad the data range, enforce a
 *  minimum span, and (optionally) clamp to a fixed band. Exported for tests. */
export function trendDomain(
  values: number[],
  opts: { clamp?: Clamp; minSpan?: number } = {},
): { lo: number; hi: number } {
  const clamp = opts.clamp === undefined ? DEFAULT_CLAMP : opts.clamp;
  const minSpan = opts.minSpan ?? DEFAULT_MIN_SPAN;
  const base = clamp ? values.map((v) => Math.max(clamp.lo, Math.min(clamp.hi, v))) : values;
  let lo = Math.min(...base);
  let hi = Math.max(...base);
  const pad = Math.max((hi - lo) * 0.15, minSpan * 0.28);
  lo -= pad;
  hi += pad;
  if (hi - lo < minSpan) {
    const mid = (hi + lo) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  }
  if (clamp) {
    lo = Math.max(clamp.lo, lo);
    hi = Math.min(clamp.hi, hi);
  }
  if (hi - lo < 1) hi = lo + 1; // degenerate guard
  return { lo, hi };
}

/** SVG geometry for a trajectory, in viewBox units. Pure + exported so the
 *  path math is unit-testable without a DOM. Returns null when there aren't
 *  enough points to draw a line. */
export function trendChartGeometry(
  points: TrendPoint[],
  height: number,
  opts: { clamp?: Clamp; minSpan?: number } = {},
): TrendGeometry | null {
  if (points.length < 2) return null;

  const times = points.map((p) => new Date(p.date).getTime());
  const tMin = times[0];
  const tMax = times[times.length - 1];
  const tSpan = tMax - tMin || 1;

  const { lo, hi } = trendDomain(
    points.map((p) => p.value),
    opts,
  );
  const span = hi - lo;

  const plotW = VW - PAD_X * 2;
  const plotH = height - PAD_Y * 2;

  const x = (t: number) => PAD_X + ((t - tMin) / tSpan) * plotW;
  const y = (value: number) =>
    PAD_Y + (1 - (Math.max(lo, Math.min(hi, value)) - lo) / span) * plotH;

  const coords = points.map((p, i) => ({ x: x(times[i]), y: y(p.value) }));
  const line = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const baseline = height - PAD_Y;
  const area = `M ${coords[0].x.toFixed(1)},${baseline.toFixed(1)} L ${line.replace(
    /,/g,
    " ",
  )} L ${coords[coords.length - 1].x.toFixed(1)},${baseline.toFixed(1)} Z`;
  const start = coords[0];
  const end = coords[coords.length - 1];

  return {
    line,
    area,
    domainLo: lo,
    domainHi: hi,
    startXFrac: start.x / VW,
    startYFrac: start.y / height,
    endXFrac: end.x / VW,
    endYFrac: end.y / height,
    zeroYFrac: lo <= 0 && hi >= 0 ? y(0) / height : null,
  };
}

// Minimum horizontal gap (fraction of width) between two date labels before
// they read as crowded. Sessions logged a day apart in a multi-week span would
// otherwise stack their labels — so crowded middle ticks get dropped.
const MIN_TICK_GAP = 0.16;

/** Time-spaced x-axis ticks, always including the first + last point, with
 *  crowded labels dropped so dates never overlap. Each is a fraction of the
 *  full chart width plus its source ISO date. Exported for tests. */
export function trendXTicks(
  points: TrendPoint[],
  count = 3,
  minGap = MIN_TICK_GAP,
): { frac: number; iso: string }[] {
  if (points.length < 2) return [];
  const times = points.map((p) => new Date(p.date).getTime());
  const tMin = times[0];
  const tMax = times[times.length - 1];
  const tSpan = tMax - tMin || 1;
  const plotW = VW - PAD_X * 2;
  const fracOf = (i: number) => (PAD_X + ((times[i] - tMin) / tSpan) * plotW) / VW;

  const n = Math.min(count, points.length);
  const idxs = [
    ...new Set(
      Array.from({ length: n }, (_, i) => Math.round((i * (points.length - 1)) / (n - 1))),
    ),
  ];

  // Keep candidates left→right while preserving the min gap; the final point is
  // always shown (drop any predecessor that would crowd it).
  const lastIdx = points.length - 1;
  const kept: { frac: number; iso: string }[] = [];
  for (const i of idxs) {
    if (i === lastIdx) continue;
    const frac = fracOf(i);
    if (kept.length === 0 || frac - kept[kept.length - 1].frac >= minGap) {
      kept.push({ frac, iso: points[i].date });
    }
  }
  const lastFrac = fracOf(lastIdx);
  while (kept.length && lastFrac - kept[kept.length - 1].frac < minGap) kept.pop();
  kept.push({ frac: lastFrac, iso: points[lastIdx].date });
  return kept;
}

const defaultFormat = (v: number) => String(Math.round(v));

export function TrendChart({
  points,
  height = 56,
  showAxis = false,
  ariaLabel = "Trajectory over time",
  className,
  emptyLabel = "Not enough history yet. Keep going.",
  clamp = DEFAULT_CLAMP,
  minSpan = DEFAULT_MIN_SPAN,
  formatValue = defaultFormat,
  color = "var(--color-accent)",
  showZeroLine = false,
}: TrendChartProps) {
  const gradientId = useId();

  if (points.length < 2) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-border bg-bg/40 px-3 text-center text-xs text-text-muted ${className ?? ""}`}
        style={{ height }}
      >
        {emptyLabel}
      </div>
    );
  }

  const geo = trendChartGeometry(points, height, { clamp, minSpan });
  if (!geo) return null; // defensive: length < 2 already handled above

  const last = points[points.length - 1];

  return (
    <div className={className}>
      <div className="relative" style={{ height }}>
        <svg
          viewBox={`0 0 ${VW} ${height}`}
          width="100%"
          height={height}
          preserveAspectRatio="none"
          role="img"
          aria-label={ariaLabel}
          style={{ display: "block", color }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>

          {showAxis &&
            [PAD_Y, height - PAD_Y].map((gy) => (
              <line
                key={gy}
                x1={PAD_X}
                x2={VW - PAD_X}
                y1={gy}
                y2={gy}
                stroke="var(--color-border)"
                strokeWidth={0.75}
              />
            ))}

          {showZeroLine && geo.zeroYFrac !== null && (
            <line
              x1={PAD_X}
              x2={VW - PAD_X}
              y1={geo.zeroYFrac * height}
              y2={geo.zeroYFrac * height}
              stroke="var(--color-border)"
              strokeWidth={1}
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}

          <path d={geo.area} fill={`url(#${gradientId})`} />
          <polyline
            points={geo.line}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* Endpoint dot as an undistorted HTML element (stays round). */}
        <span
          className="absolute block h-2.5 w-2.5 rounded-full ring-2 ring-surface"
          style={{
            left: `${geo.endXFrac * 100}%`,
            top: `${geo.endYFrac * 100}%`,
            transform: "translate(-50%, -50%)",
            background: color,
          }}
        />

        {/* Actual start + end VALUES at the line's endpoints, so the numbers
            reconcile with the delta headline instead of the padded axis bounds. */}
        {showAxis && (
          <>
            <span
              className="absolute rounded bg-surface/90 px-1 text-[11px] tabular-nums text-text-muted"
              style={{
                left: `${geo.startXFrac * 100}%`,
                top: `${geo.startYFrac * 100}%`,
                transform: "translate(0, -150%)",
              }}
            >
              {formatValue(points[0].value)}
            </span>
            <span
              className="absolute rounded bg-surface/90 px-1 text-[11px] font-semibold tabular-nums text-text"
              style={{
                left: `${geo.endXFrac * 100}%`,
                top: `${geo.endYFrac * 100}%`,
                transform: `translate(${geo.endXFrac > 0.85 ? "-100%" : "-50%"}, -150%)`,
              }}
            >
              {formatValue(last.value)}
            </span>
          </>
        )}
      </div>

      {showAxis && (
        <div className="relative mt-1.5 h-4 text-[10px] text-text-muted">
          {trendXTicks(points, 3).map((t) => (
            <span
              key={t.iso}
              className="absolute whitespace-nowrap tabular-nums"
              style={{
                left: `${t.frac * 100}%`,
                transform: `translateX(${
                  t.frac < 0.15 ? "0" : t.frac > 0.85 ? "-100%" : "-50%"
                })`,
              }}
            >
              {shortDate(t.iso)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
